import React from 'react';

import { type Control, type FieldValues, type Path, useWatch } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';

import { CARD_TYPES } from 'src/features/cards/types';
import { useGetParkingGuestReasons } from 'src/features/parkingManagement/api/ReasonsForGuest/useGetParkingGuestReasons';
import { useGetParking } from 'src/features/parkingManagement/api/useGetParking';
import { useListPricingPlans } from 'src/features/pricingPlans/api/useListPricingPlans';
import { ALLOWED_VALIDATION, MEMBERSHIP_ROLES } from 'src/features/userManagement/types';
import { membershipRoleOptions, userManagementMessages } from 'src/features/userManagement/utils';
import { useParking } from 'src/providers/ParkingProvider';
import { FormControl, FormControlStack, FormField, FormItem, FormLabel, FormMessage } from 'src/registry/ui/Form';
import {
    MultiSelect,
    MultiSelectControl,
    MultiSelectTrigger,
    MultiSelectContent,
    MultiSelectOption,
} from 'src/registry/ui/MultiSelect';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'src/registry/ui/Select';

type CommonFormFieldsProps<T extends FieldValues> = {
    control: Control<T>;
    fieldNames: { role: keyof T; validator_type: keyof T; validator_pricing_plan: keyof T };
    children?: React.ReactNode;
};

function CommonFormFields<T extends FieldValues>(props: CommonFormFieldsProps<T>) {
    const { control, fieldNames, children } = props;

    const { formatMessage } = useIntl();
    const { parkingId } = useParking();
    const allowedRoles: MEMBERSHIP_ROLES[] = useGetParking({ parkingId }).data?.roles || [];
    const { data: pricingPlanData } = useListPricingPlans({ parkingId, cardType: CARD_TYPES.NORMAL, limit: 100 });

    const translationMapping = {
        [ALLOWED_VALIDATION.GUEST]: userManagementMessages.guest,
        [ALLOWED_VALIDATION.PRICING_PLAN]: userManagementMessages.pricingPlan,
    };

    const role = useWatch({ control, name: fieldNames.role as Path<T> });
    const validatorType = useWatch({ control, name: fieldNames.validator_type as Path<T> });
    const isValidator = role === MEMBERSHIP_ROLES.VALIDATOR;
    const isValidatorAndPricingPlan = isValidator && validatorType === ALLOWED_VALIDATION.PRICING_PLAN;

    const allowedRolesList = Object.values(membershipRoleOptions).filter(
        (role) => role.value !== MEMBERSHIP_ROLES.CLIENT && allowedRoles.includes(role.value),
    );

    const { data: guestReasonsData } = useGetParkingGuestReasons({
        parkingId: parkingId || '',
        config: { enabled: !!parkingId },
    });

    return (
        <>
            {children}
            <FormField
                control={control}
                name={fieldNames.role as Path<T>}
                render={({ field }) => (
                    <FormItem variant="dialog">
                        <FormLabel variant="dialog" required>
                            {formatMessage(userManagementMessages.role)}
                        </FormLabel>
                        <FormControlStack className="mr-3">
                            <FormControl>
                                <Select onValueChange={field.onChange} value={field.value}>
                                    <SelectTrigger className="w-full">
                                        <SelectValue
                                            placeholder={formatMessage(userManagementMessages.rolePlaceholder)}
                                        />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {allowedRolesList.map((role) => (
                                            <SelectItem key={role.value} value={role.value}>
                                                {formatMessage(role.label)}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </FormControl>
                            <FormMessage />
                        </FormControlStack>
                    </FormItem>
                )}
            />
            {isValidator && (
                <FormField
                    control={control}
                    name={fieldNames.validator_type as Path<T>}
                    render={({ field }) => (
                        <FormItem variant="dialog">
                            <FormLabel variant="dialog" required={isValidator}>
                                {formatMessage(userManagementMessages.validationType)}
                            </FormLabel>
                            <FormControlStack className="mr-3">
                                <FormControl>
                                    <Select onValueChange={field.onChange} value={field.value ?? undefined}>
                                        <SelectTrigger className="w-full">
                                            <SelectValue
                                                placeholder={formatMessage(userManagementMessages.validationType)}
                                            />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {[...Object.values(ALLOWED_VALIDATION)].map((value) => (
                                                <SelectItem key={value} value={value}>
                                                    {formatMessage(translationMapping[value])}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </FormControl>
                                <FormMessage />
                            </FormControlStack>
                        </FormItem>
                    )}
                />
            )}
            {isValidatorAndPricingPlan && (
                <FormField
                    control={control}
                    name={fieldNames.validator_pricing_plan as Path<T>}
                    render={({ field }) => (
                        <FormItem variant="dialog">
                            <FormLabel variant="dialog" required>
                                {formatMessage(userManagementMessages.pricingPlanValidation)}
                            </FormLabel>
                            <FormControlStack className="mr-3">
                                <FormControl>
                                    <Select onValueChange={field.onChange} value={field.value ?? undefined}>
                                        <SelectTrigger className="w-full">
                                            <SelectValue
                                                placeholder={formatMessage(userManagementMessages.pricingPlan)}
                                            />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {pricingPlanData?.results.map((plan) => (
                                                <SelectItem key={plan.id} value={plan.id.toString()}>
                                                    {plan.plan_name}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </FormControl>
                                <FormMessage />
                            </FormControlStack>
                        </FormItem>
                    )}
                />
            )}
            {validatorType === ALLOWED_VALIDATION.GUEST && (
                <FormField
                    control={control}
                    name="guest_reasons"
                    render={({ field }) => {
                        // Remove ref from field to avoid passing it to the MultiSelect component
                        const { ref: _, ...rest } = field;

                        const fieldData = field.value
                            .map((reason) => guestReasonsData?.results.find((r) => r.id === reason)?.name)
                            .join(', ');

                        const allValues = guestReasonsData?.results.map((point) => point.id);
                        return (
                            <FormItem variant="dialog">
                                <FormLabel variant="dialog">
                                    <FormattedMessage
                                        id={userManagementMessages.guestReasons.id}
                                        defaultMessage={userManagementMessages.guestReasons.defaultMessage}
                                    />
                                </FormLabel>
                                <FormControlStack>
                                    <FormControl>
                                        <MultiSelect {...rest}>
                                            <MultiSelectTrigger asChild>
                                                <MultiSelectControl>
                                                    {fieldData}
                                                    {fieldData.length == 0 && (
                                                        <span className="text-gray-400">
                                                            <FormattedMessage
                                                                id={userManagementMessages.guestReasons.id}
                                                                defaultMessage={
                                                                    userManagementMessages.guestReasons.defaultMessage
                                                                }
                                                            />
                                                            ...
                                                        </span>
                                                    )}
                                                </MultiSelectControl>
                                            </MultiSelectTrigger>
                                            <MultiSelectContent allValues={allValues}>
                                                {guestReasonsData?.results.map((reason) => (
                                                    <MultiSelectOption key={reason.id} selected={reason.id}>
                                                        {reason.name}
                                                    </MultiSelectOption>
                                                ))}
                                            </MultiSelectContent>
                                        </MultiSelect>
                                    </FormControl>
                                    <FormMessage />
                                </FormControlStack>
                            </FormItem>
                        );
                    }}
                />
            )}
        </>
    );
}

export default CommonFormFields;
